import { createApp, watch } from 'vue';

import { createPinia, storeToRefs } from 'pinia';

import { abilitiesPlugin } from '@casl/vue';
import '@core/scss/template/index.scss';
import Aura from '@primevue/themes/aura';
import * as Sentry from '@sentry/vue';
import '@styles/styles.scss';
import { createHead } from '@vueuse/head';
import { Chart as ChartJS } from 'chart.js';
import LogRocket from 'logrocket';

import PrimeVue from 'primevue/config';
import ConfirmationService from 'primevue/confirmationservice';
import FocusTrap from 'primevue/focustrap';
import ToastService from 'primevue/toastservice';
import Tooltip from 'primevue/tooltip';

import 'primeicons/primeicons.css';

import { $axios, $axiosPublic, $axiosOlap, $axiosAmazonAds } from '@/api/$axios';
import { getSentryRuntimeConfig } from '@/config/sentry';
import { hasSensitiveAuthBody, sanitizeLogRocketNetwork, SENTRY_REPLAY_OPTIONS } from '@/config/sessionReplay';
import { readSansFontFamily, onSansFontChange } from '@/helpers/fonts';
import { redactSensitiveQuery } from '@/helpers/urlHelpers';
import ability from '@/plugins/casl/ability';
import '@/plugins/dayjs';
import i18n, { loadLocaleMessages } from '@/plugins/i18n';
import layoutsPlugin from '@/plugins/layouts';
import vuetify from '@/plugins/vuetify';
import { installAuth } from '@/services/auth/bootstrap';
import { useAppVersionStore } from '@/stores/appVersion';
import { useAuthStore } from '@/stores/auth';
import { labelInternalTraffic, markInternalTraffic } from '@/utils/internalTraffic';


import App from './App.vue';
import './assets/css/main.scss';
import directives from './directives/';
import { setupChatwoot } from './plugins/chatwoot';
import { setupGoogleAnalytics } from './plugins/google-analytics';
import { installTranslateCrashGuard } from './plugins/translateCrashGuard';
import router from './router';

// Before anything renders: harden Node.removeChild/insertBefore against Google
// Translate's text-node rewrites so page-translate can't white-screen the app.
installTranslateCrashGuard();

// Chart.js renders to canvas, so it can't read the --wp-font-sans CSS variable —
// mirror the resolved token into its default so chart labels match the rest of the UI
// instead of Chart.js's Helvetica/Arial default. The token is viewport-conditional
// (tokens.scss swaps the body face on narrow screens), hence the subscription: the
// default is re-read whenever the resolved stack changes. Chart.js resolves fonts at
// draw time, and a viewport change is by definition a resize, so responsive charts
// redraw with the new family on the same event.
ChartJS.defaults.font.family = readSansFontFamily();
onSansFontChange((family) => {
  ChartJS.defaults.font.family = family;
});

const app = createApp(App);

const hostname = window.location.hostname;
const sentryRuntimeConfig = getSentryRuntimeConfig({
  hostname,
  isProdBuild: import.meta.env.PROD,
});
const { isProd } = sentryRuntimeConfig;

// Shared by Sentry beforeSend and the chunk-load handlers below — must be defined before init.
const isChunkLoadError = (message) => {
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Loading chunk') ||
    message.includes('Loading CSS chunk') ||
    message.includes('Unable to preload CSS')
  );
};

Sentry.init({
  app,
  dsn: 'https://f96ff38e12967a95424a61c0a4dbf163@o4509110744711168.ingest.us.sentry.io/4509110752772096',
  tracesSampleRate: sentryRuntimeConfig.tracesSampleRate,
  enabled: sentryRuntimeConfig.enabled,
  environment: sentryRuntimeConfig.environment,
  integrations: [
    Sentry.replayIntegration(SENTRY_REPLAY_OPTIONS),
  ],
  // Session Replay
  replaysSessionSampleRate: sentryRuntimeConfig.replaysSessionSampleRate,
  replaysOnErrorSampleRate: sentryRuntimeConfig.replaysOnErrorSampleRate,
  release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
  beforeSend(event, hint) {
    const ex = hint?.originalException;
    // A third-party <script> in <head> (gtag/fbq/chatwoot) blocked by an ad-blocker rejects
    // its load promise with the raw DOM error Event (not an Error), surfacing as an unhandled
    // rejection. It is not actionable, so drop it here — independent of listener order.
    // Real Vite chunk failures are re-captured as a proper Error in handleChunkError below.
    if (ex instanceof Event && ex.type === 'error' && ex.target instanceof HTMLScriptElement) {
      return null;
    }
    // Raw chunk/CSS-preload rejection — a duplicate: handleChunkError below re-captures the
    // same failure wrapped with its source ("Chunk load error (router): …").
    if (ex instanceof Error && !ex.message.startsWith('Chunk load error') && isChunkLoadError(ex.message)) {
      return null;
    }
    // Expected session-expiry/logout transition (session.js / tokenResolver.js) — keep it
    // visible but out of the error inbox. ERR_AUTH_LOGOUT stays level:error on purpose:
    // a spike of forced logouts is a real incident signal (WP-981).
    if (ex instanceof Error && ex.code === 'ERR_NO_SESSION') {
      event.level = 'info';
    }
    // An error on an OAuth landing page would report its URL with the one-time claim token
    // or auth code in the query.
    if (event.request?.url) event.request.url = redactSensitiveQuery(event.request.url);
    return event;
  },
  // Never let an /auth/* or /oauth/* request/response body (password, OTP code, tokens,
  // one-time claim tokens, OAuth state) ride along in an HTTP/fetch/xhr breadcrumb
  // attached to an error event.
  beforeBreadcrumb(breadcrumb) {
    const isHttp = ['http', 'fetch', 'xhr'].includes(breadcrumb.category);
    const url = breadcrumb.data?.url || '';
    if (isHttp && hasSensitiveAuthBody(url) && breadcrumb.data) {
      const { body, request_body: _rb, response_body: _rsb, ...rest } = breadcrumb.data;
      void body;
      breadcrumb.data = rest;
    }
    // Nor the OAuth landing URL's claim token / auth code in a navigation breadcrumb.
    if (breadcrumb.category === 'navigation' && breadcrumb.data) {
      breadcrumb.data.from = redactSensitiveQuery(breadcrumb.data.from);
      breadcrumb.data.to = redactSensitiveQuery(breadcrumb.data.to);
    }
    return breadcrumb;
  },
});

if (isProd) {
  // Auth bodies, Authorization headers, and eBay buyer/message/dataset bodies: see
  // sanitizeLogRocketNetwork. Headers and status are kept.
  LogRocket.init('vnlru2/wiseppc-app', {
    // The OAuth landing pages arrive with a one-time claim token / auth code in the query.
    browser: { urlSanitizer: redactSensitiveQuery },
    network: {
      requestSanitizer: sanitizeLogRocketNetwork,
      responseSanitizer: sanitizeLogRocketNetwork,
    },
  });
}

setupGoogleAnalytics(app);
setupChatwoot();
labelInternalTraffic();

const head = createHead();

// Fix issues don't hide it.
// const originalWarn = console.warn
// console.warn = function (...args) {
//   if (!args[0]?.includes('AG Grid:')) {
//     originalWarn.apply(console, args)
//   }
// }

// ModuleRegistry.registerModules([AllCommunityModule])

app.use(head);
app.use(PrimeVue, {
  // Default theme configuration
  theme: {
    preset: Aura,
    options: {
      prefix: 'p',
      darkModeSelector: '.dark-theme',
      cssLayer: false,
    },
  },
});
app.use(createPinia());
app.use(vuetify);
app.use(router);
app.use(i18n);
app.use(layoutsPlugin);
app.use(abilitiesPlugin, ability, {
  useGlobalProperties: true,
});
app.use(ConfirmationService);
app.directive('tooltip', Tooltip);
app.directive('focustrap', FocusTrap);
app.use(ToastService);

directives(app);

// Wire auth bootstrap: capture OAuth/invite/referral params, connect cross-tab sync, hydrate
// tokens, restore the backend session. Fire-and-forget — the router guard awaits
// bootstrap.waitForReady() before its first decision (only the guard awaits it).
installAuth(router);

// Label staff sessions as internal GA4 traffic: once the restored user is a super admin, drop the
// shared cookie and label this session's events traffic_type=internal. Sync flush so it is set
// before the first navigation's page_view is reported.
watch(
  () => useAuthStore().user?.superAdmin === true,
  (isStaff) => {
    if (isStaff) markInternalTraffic();
  },
  { immediate: true, flush: 'sync' },
);

window.storeToRefs = storeToRefs;

window.$axios = $axios;
window.$axiosPublic = $axiosPublic;
window.$axiosOlap = $axiosOlap;
window.$axiosAmazonAds = $axiosAmazonAds;

// Handle chunk loading errors after a new deployment removes old hashed chunks.
// If /version.json shows a newer version than what's loaded — reload automatically.
// Otherwise fall back to the existing reload-confirmation dialog.
let chunkErrorHandled = false;

const handleChunkError = async (errorMessage, source) => {
  if (chunkErrorHandled) return;

  // In dev, "Failed to fetch dynamically imported module" is a stale Vite chunk after an
  // HMR / branch switch / dep re-optimize — not a real deploy. Don't show the update prompt.
  if (import.meta.env.DEV) {
    console.warn(`[dev] stale dynamic import ignored (${source}):`, errorMessage);
    return;
  }

  chunkErrorHandled = true;

  console.warn(`Chunk load error detected (${source}):`, errorMessage);
  Sentry.captureException(new Error(`Chunk load error (${source}): ${errorMessage}`));

  const versionStore = useAppVersionStore();
  const hasNewVersion = await versionStore.checkVersion();
  if (hasNewVersion) {
    await versionStore.clearCachesAndReload();
    return;
  }

  window.dispatchEvent(new CustomEvent('chunkLoadError'));
};

window.addEventListener('chunkLoadErrorDismissed', () => {
  chunkErrorHandled = false;
});

window.addEventListener('error', (event) => {
  const errorMessage = event.message || event.error?.message || '';
  if (isChunkLoadError(errorMessage)) {
    handleChunkError(errorMessage, 'error');
  }
});

window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;

  // Script load failure: <script> in <head> fails to load. The browser rejects the implicit
  // Promise with the raw error Event — not an Error object, so String(reason) = "[object Event]"
  // bypasses isChunkLoadError.
  if (reason instanceof Event && reason.type === 'error' && reason.target instanceof HTMLScriptElement) {
    event.preventDefault();
    const src = reason.target.src || '';
    // Only a same-origin app module/chunk means a stale deployment. Third-party scripts
    // (googletagmanager, fbevents, chatwoot) blocked by ad-blockers are not a reason to
    // prompt a reload — ignore them.
    const isAppModule =
      reason.target.type === 'module' ||
      (!!src && new URL(src, window.location.href).origin === window.location.origin);
    if (isAppModule) {
      handleChunkError('Module script failed to load', 'promise');
    }
    return;
  }

  const errorMessage = reason?.message || String(reason) || '';
  if (isChunkLoadError(errorMessage)) {
    handleChunkError(errorMessage, 'promise');
  }
});

// Vue Router catches dynamic import() failures internally and may not propagate them
// to unhandledrejection — router.onError() is the authoritative catch point for route-level chunks
router.onError((error) => {
  const errorMessage = error?.message || String(error);
  if (isChunkLoadError(errorMessage)) {
    handleChunkError(errorMessage, 'router');
  }
});

// app.config.errorHandler = (err, instance, info) => {
//   console.error('Vue Error:', err)
//   console.error('Component:', instance)
//   console.error('Error Info:', info)
// }

// Locales are lazy-loaded; ensure the resolved initial locale's messages are present before the
// first paint so there is no flash of fallback English. For en (the default) this is a no-op.
loadLocaleMessages(i18n.global.locale.value).finally(() => app.mount('#app'));
